import { useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { api, ApiError } from "@/lib/api"
import { useAppSelector } from "@/store"
import {
  MAX_SNAPSHOT_BYTES, SNAPSHOT_ACCEPT, SNAPSHOT_EXTENSION, fullName, hasSnapshotExtension,
  incompleteNotice, prepareSnapshot, receiptMatches, snapshotIdentity,
  type SnapshotIdentity, type SnapshotReceipt, type SnapshotReview, type SnapshotUpload,
} from "./snapshot"

function formatMoney(value: number | null) {
  return value === null ? "Not reported" : value.toLocaleString(undefined, { style: "currency", currency: "USD" })
}

export default function PseSnapshotImportPage() {
  const permissions = useAppSelector(state => state.permissions.permissions)
  const allowed = permissions.includes("pse-online:manage") || permissions.includes("admin:manage-access")
  const [pending, setPending] = useState<SnapshotUpload | null>(null)
  const [identity, setIdentity] = useState<SnapshotIdentity | null>(null)
  const [receipt, setReceipt] = useState<SnapshotReceipt | null>(null)
  const [review, setReview] = useState<SnapshotReview | null>(null)
  const [message, setMessage] = useState("")
  const [busy, setBusy] = useState(false)
  const [uncertain, setUncertain] = useState(false)
  const [reconciledMissing, setReconciledMissing] = useState(false)
  const [receiptId, setReceiptId] = useState("")
  const gate = useRef(false)

  function reset() {
    setPending(null); setIdentity(null); setReceipt(null); setReview(null)
    setUncertain(false); setReconciledMissing(false); setReceiptId(""); setMessage("")
  }

  async function selectFile(file?: File) {
    if (!file || gate.current) return
    if (file.size > MAX_SNAPSHOT_BYTES) { setMessage("Snapshot exceeds 2 MiB. Nothing was uploaded."); return }
    gate.current = true; setBusy(true)
    try {
      const text = await file.text()
      const upload = await prepareSnapshot(text)
      setPending(upload); setIdentity(snapshotIdentity(text)); setReceipt(null); setReview(null)
      setReceiptId(upload.uploadId); setUncertain(false)
      setMessage(hasSnapshotExtension(file.name)
        ? "Snapshot file read. Review the identity, then import it."
        : `This file does not end in ${SNAPSHOT_EXTENSION}, but its contents are a valid snapshot. Review the identity before importing.`)
    } catch (error) {
      setPending(null); setIdentity(null)
      setMessage(error instanceof Error ? error.message : "Unreadable file. Nothing was uploaded.")
    } finally { gate.current = false; setBusy(false) }
  }

  async function loadReview(id: string) {
    try {
      setReview(await api.get<SnapshotReview>(`/api/pse-online/snapshots/${id}/review`))
    } catch {
      setMessage("Snapshot stored, but the review could not be loaded. The stored snapshot is unaffected; reload to try again.")
    }
  }

  async function store() {
    if (!pending || gate.current || (uncertain && !reconciledMissing)) return
    gate.current = true; setBusy(true); setReconciledMissing(false); setMessage("Importing snapshot…")
    try {
      const result = await api.post<SnapshotReceipt>("/api/pse-online/snapshots", pending)
      if (!receiptMatches(result, pending)) {
        setUncertain(false)
        setMessage("CMS returned a receipt for a different snapshot. Nothing was retried; check the upload ID before importing again.")
        return
      }
      setReceipt(result); setUncertain(false)
      setMessage("Snapshot imported. No people, benefits, or CSV records were changed.")
      await loadReview(result.snapshotId)
    } catch (error) {
      const knownRejection = error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 408
      setUncertain(!knownRejection)
      setMessage(knownRejection && error instanceof ApiError ? error.message
        : "No confirmed receipt. The snapshot may have been imported. Check its receipt before importing again; nothing is retried automatically.")
    } finally { gate.current = false; setBusy(false) }
  }

  async function checkReceipt() {
    if (gate.current || !/^[0-9a-f-]{36}$/i.test(receiptId)) return
    gate.current = true; setBusy(true); setReconciledMissing(false)
    try {
      const result = await api.get<SnapshotReceipt>(`/api/pse-online/snapshots/${receiptId}`)
      setReceipt(result); setUncertain(false)
      setMessage("CMS holds a stored snapshot for this upload ID.")
      await loadReview(result.snapshotId)
    } catch (error) {
      const missing = error instanceof ApiError && error.status === 404
      setReconciledMissing(missing)
      setMessage(missing
        ? "No receipt is visible for this upload and account. You may import the same file again; its upload ID and bytes will not change."
        : "Receipt lookup failed. Nothing was imported or retried.")
    } finally { gate.current = false; setBusy(false) }
  }

  if (!allowed) return <p role="alert" className="p-6">PSE management permission is required to import source snapshots.</p>

  const summary = review?.summary
  return <div className="flex max-w-4xl flex-col gap-4 p-6">
    <h1 className="text-2xl font-bold">Import PSE snapshot</h1>
    <p className="text-muted-foreground">
      Import a <code>{SNAPSHOT_EXTENSION}</code> file saved by the PSE Companion desktop app. CMS stores the source
      snapshot and suggests existing people. It does not create or change people, benefits, or CSV import records.
    </p>

    <Card>
      <CardHeader>
        <CardTitle>Choose a snapshot file</CardTitle>
        <CardDescription>Export the application in PSE Companion, choose Save snapshot, then select that file here. Nothing is uploaded until you choose Import.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Label htmlFor="snapshot-file">Snapshot file ({SNAPSHOT_EXTENSION})</Label>
        <Input id="snapshot-file" data-testid="snapshot-file" type="file" accept={SNAPSHOT_ACCEPT} disabled={busy}
          onChange={event => { void selectFile(event.target.files?.[0]) }} />
        {identity && <p data-testid="snapshot-identity">
          Application {identity.applicationId} · Program year {identity.programYear} · Agency {identity.agencyId} · {identity.objects} source records
        </p>}
        {pending && <p className="break-all text-sm text-muted-foreground">Upload ID: {pending.uploadId}</p>}
        <div className="flex gap-2">
          <Button data-testid="import-snapshot" disabled={busy || !pending || (uncertain && !reconciledMissing) || !!receipt} onClick={() => { void store() }}>
            {uncertain ? "Retry the same import" : "Import snapshot"}
          </Button>
          <Button variant="outline" disabled={busy} onClick={reset}>Start over</Button>
        </div>
      </CardContent>
    </Card>

    {summary && <Card>
      <CardHeader><CardTitle>Imported application</CardTitle><CardDescription>Source values as PSE recorded them.</CardDescription></CardHeader>
      <CardContent className="flex flex-col gap-2">
        <p>Application {summary.applicationId} · Program year {summary.programYear} · Agency {summary.agencyName ?? summary.agencyId}</p>
        {incompleteNotice(summary) && <p data-testid="incomplete-notice" role="note">{incompleteNotice(summary)}</p>}
        <p>Applicant: {[summary.applicantFirstName, summary.applicantLastName].filter(Boolean).join(" ") || "Not recorded"}
          {summary.applicantDateOfBirth ? ` · Born ${summary.applicantDateOfBirth}` : ""}</p>
        <p>{[summary.city, summary.state, summary.postalCode].filter(Boolean).join(", ") || "No address recorded"}</p>
        <p>PSE status: {summary.status ?? "Not recorded"}{summary.statusReason ? ` (${summary.statusReason})` : ""}</p>
        <p>Household members: {summary.householdMemberCount} · Income records: {summary.incomeRecordCount} · Reported monthly income: {formatMoney(summary.reportedMonthlyIncome)}</p>
        <p>Pledges: electric {formatMoney(summary.electricPledge)} · gas {formatMoney(summary.gasPledge)}</p>
        <p className="text-sm text-muted-foreground">Captured {new Date(summary.capturedAt).toLocaleString()} · {summary.sourceObjectCount} source records · Social Security numbers are not displayed.</p>
      </CardContent>
    </Card>}

    {review && <Card>
      <CardHeader>
        <CardTitle>Possible existing people</CardTitle>
        <CardDescription>Suggestions only. Nothing is linked, created, or changed by this page.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {review.matchState !== "matched" && <p role="alert" data-testid="match-unavailable">{review.matchUnavailableReason}</p>}
        {review.household.map((member, index) => <div key={index} className="grid gap-4 rounded border p-4 md:grid-cols-2">
          <div className="rounded-md border bg-muted/30 p-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Online (PSE Snapshot)</p>
            <p className="font-medium">{fullName(member)}{member.isApplicant ? " · applicant" : ""}{member.isDisabled ? " · disabled" : ""}</p>
            <p className="text-sm text-muted-foreground">
              {member.dateOfBirth ? `Born ${member.dateOfBirth}` : "No date of birth"}{member.ssnLast4 ? ` · SSN ending ${member.ssnLast4}` : ""}
            </p>
          </div>
          <div className="rounded-md border p-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">CMS (Matched People)</p>
            {review.matchState === "matched" && (member.candidates.length === 0
              ? <p className="text-sm text-muted-foreground">No match found.</p>
              : <ul className="flex flex-col gap-2">{member.candidates.map(candidate => <li key={candidate.personId} className="rounded border bg-background p-2 text-sm">
                <div className="font-medium">
                  {candidate.firstName} {candidate.lastName}
                  {candidate.dateOfBirth ? ` · DOB ${candidate.dateOfBirth}` : ""}
                  {review.authorizedIdentifiers?.[candidate.personId]
                    ? ` · SSN ${review.authorizedIdentifiers[candidate.personId]}`
                    : candidate.ssnLast4 ? ` · SSN ending ${candidate.ssnLast4}` : ""}
                  {` · ${Math.round(candidate.confidenceScore * 100)}% ${candidate.matchType}`}
                  {candidate.matchedFields.length > 0 ? ` (${candidate.matchedFields.join(", ")})` : ""}
                </div>
                {candidate.previousApplications && candidate.previousApplications.length > 0 && (
                  <div className="mt-1 space-y-0.5 border-t pt-1 text-xs">
                    {candidate.previousApplications.map(app => (
                      <a
                        key={app.benefitId}
                        href={`/benefit-applications/${app.benefitId}`}
                        target="_blank"
                        rel="noreferrer"
                        className="block text-primary underline-offset-2 hover:underline"
                      >
                        {app.fileNumber > 0 ? `File #${app.fileNumber}` : "File number unavailable"} · PY {app.programYear} · {app.address}
                      </a>
                    ))}
                  </div>
                )}
              </li>)}</ul>)}
          </div>
        </div>)}
      </CardContent>
    </Card>}

    <Card>
      <CardHeader><CardTitle>Receipt or interrupted import</CardTitle><CardDescription>Look up what CMS already stored for an upload ID.</CardDescription></CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Label htmlFor="receipt-id">Upload ID</Label>
        <Input id="receipt-id" value={receiptId} readOnly={!!pending} onChange={event => setReceiptId(event.target.value)} />
        <Button variant="outline" disabled={busy || !/^[0-9a-f-]{36}$/i.test(receiptId)} onClick={() => { void checkReceipt() }}>Check CMS receipt</Button>
        {receipt && <p data-testid="snapshot-receipt">Stored snapshot {receipt.snapshotId} at {receipt.storedAt}. Transfer SHA-256: {receipt.sha256}</p>}
      </CardContent>
    </Card>

    {message && <p role="status">{message}</p>}
    <p className="text-sm text-muted-foreground">
      The chosen file stays in this tab until you import it. After an uncertain import, keep the upload ID and check its receipt instead of importing a second copy.
    </p>
  </div>
}
